import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';

let uid = 0;

/**
 * <cadence-input label="Email" type="email"> — renders a labelled input with
 * proper for/id association. Forwards value via the `value` property and emits
 * `input` on change and `enter` on Enter key (so host forms can submit).
 */
@customElement('cadence-input')
export class CadenceInput extends LitElement {
  @property() label = '';
  @property() type = 'text';
  @property() name = '';
  @property() value = '';
  @property() placeholder = '';
  @property() autocomplete = '';
  @property({ type: Boolean }) required = false;
  @property() error = '';

  private inputId = `cadence-input-${++uid}`;

  static styles = css`
    :host { display: block; }
    .field { display: flex; flex-direction: column; gap: 6px; }
    label { font-size: 0.85rem; font-weight: 600; color: var(--color-text-secondary); }
    input {
      font: inherit;
      padding: 10px 12px;
      border-radius: var(--radius-md);
      border: 1px solid var(--color-border);
      background: var(--color-bg-primary);
      color: var(--color-text-primary);
      transition: border-color 0.15s, box-shadow 0.15s;
    }
    input:focus {
      outline: none;
      border-color: var(--color-primary);
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-primary) 25%, transparent);
    }
    input[aria-invalid='true'] { border-color: var(--color-danger); }
    .error { color: var(--color-danger); font-size: 0.8rem; }
  `;

  private onInput(e: Event) {
    // Stop the native input event at the shadow boundary; we re-emit a composed
    // CustomEvent so the host sees exactly one event carrying the value in detail.
    e.stopPropagation();
    this.value = (e.target as HTMLInputElement).value;
    this.dispatchEvent(new CustomEvent('input', { detail: this.value, bubbles: true, composed: true }));
  }

  private onKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') this.dispatchEvent(new CustomEvent('enter', { bubbles: true, composed: true }));
  }

  render() {
    return html`
      <div class="field">
        ${this.label ? html`<label for=${this.inputId}>${this.label}</label>` : ''}
        <input
          id=${this.inputId}
          name=${this.name}
          type=${this.type}
          .value=${this.value}
          placeholder=${this.placeholder}
          autocomplete=${this.autocomplete || 'off'}
          ?required=${this.required}
          aria-invalid=${this.error ? 'true' : 'false'}
          @input=${this.onInput}
          @keydown=${this.onKeydown}
        />
        ${this.error ? html`<span class="error" role="alert">${this.error}</span>` : ''}
      </div>
    `;
  }
}
